import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import {
  X, Send, Save, Image as ImageIcon, ArrowLeft,
  Trophy, Target
} from 'lucide-react';

export default function CreateCoupon() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [sendAfterCreate, setSendAfterCreate] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const [form, setForm] = useState({
    home_team: '',
    away_team: '',
    league: '',
    match_date: '',
    odds: '',
    prediction: '',
    description: '',
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const res = await axios.post('/api/coupons', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Coupon created successfully!');

      if (sendAfterCreate && res.data.id) {
        try {
          const sendRes = await axios.post(`/api/coupons/${res.data.id}/send/telegram`);
          toast.success(`Sent to Telegram! Success: ${sendRes.data.success}, Failed: ${sendRes.data.failed}`);
        } catch (sendErr) {
          toast.error('Telegram send failed');
        }
      }

      navigate('/coupons');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create coupon');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto space-y-6 animate-fadeInUp">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-xl md:text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
            Create New Coupon
          </h1>
          <p className="text-sm text-muted-foreground">Enter match details and your prediction</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid md:grid-cols-2 gap-5">
          {/* Match Info */}
          <Card className="bg-card/80 border-border/60 backdrop-blur">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Trophy className="w-4 h-4 text-primary" />
                Match Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="home_team">Home Team</Label>
                <Input
                  id="home_team"
                  name="home_team"
                  data-testid="coupon-form-match-input"
                  value={form.home_team}
                  onChange={handleChange}
                  placeholder="e.g. Galatasaray"
                  className="bg-muted/30 border-border/70"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="away_team">Away Team</Label>
                <Input
                  id="away_team"
                  name="away_team"
                  data-testid="coupon-form-away-input"
                  value={form.away_team}
                  onChange={handleChange}
                  placeholder="e.g. Fenerbahce"
                  className="bg-muted/30 border-border/70"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="league">League</Label>
                <Input
                  id="league"
                  name="league"
                  data-testid="coupon-form-league-input"
                  value={form.league}
                  onChange={handleChange}
                  placeholder="e.g. Super Lig"
                  className="bg-muted/30 border-border/70"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="match_date">Match Date</Label>
                <Input
                  id="match_date"
                  name="match_date"
                  type="date"
                  data-testid="coupon-form-date-button"
                  value={form.match_date}
                  onChange={handleChange}
                  className="bg-muted/30 border-border/70"
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Prediction & Details */}
          <Card className="bg-card/80 border-border/60 backdrop-blur">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" />
                Prediction & Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="odds">Odds</Label>
                <Input
                  id="odds"
                  name="odds"
                  data-testid="coupon-form-odds-input"
                  value={form.odds}
                  onChange={handleChange}
                  placeholder="e.g. 1.85"
                  className="bg-muted/30 border-border/70 font-mono"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prediction">Prediction</Label>
                <Input
                  id="prediction"
                  name="prediction"
                  data-testid="coupon-form-prediction-select"
                  value={form.prediction}
                  onChange={handleChange}
                  placeholder="e.g. Home Win, BTTS, Over 2.5"
                  className="bg-muted/30 border-border/70"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  data-testid="coupon-form-description-textarea"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Additional info about this coupon..."
                  className="bg-muted/30 border-border/70 min-h-[80px]"
                  rows={3}
                />
              </div>

              {/* Image Upload */}
              <div className="space-y-2">
                <Label>Image</Label>
                {imagePreview ? (
                  <div className="relative rounded-xl overflow-hidden border border-border/60">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-40 object-cover"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <div
                    className="dropzone"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <ImageIcon className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Click to upload</p>
                    <p className="text-xs text-muted-foreground mt-1">PNG, JPG, WEBP</p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  data-testid="coupon-form-image-upload-input"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <Separator className="my-5 opacity-50" />

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={sendAfterCreate}
              onChange={(e) => setSendAfterCreate(e.target.checked)}
              className="rounded border-border"
            />
            Send to Telegram after creating
          </label>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="coupon-form-submit-button"
              disabled={loading}
              className="gap-2"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
              ) : sendAfterCreate ? (
                <><Send className="w-4 h-4" /> Create & Send</>
              ) : (
                <><Save className="w-4 h-4" /> Save</>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
